import { useContext, useCallback, useMemo } from 'react';
import { gutters } from 'app/spacing';
import WizardStateContext from 'components/Wizards/shared/context/wizardstate';
import { REPORT_TYPES } from 'components/Wizards/Dashlet/types';
import TitleField from '../../fields/title';
import { StyledWizardField } from 'components/Wizards/shared/styles';
import KizenTypography from 'app/kizentypo';
import ButtonGroup from 'components/Inputs/ButtonGroup';
import { useTranslation } from 'react-i18next';
import useDefaultValues from 'components/Wizards/shared/hooks/useDefaultValue';
import Switch from 'components/Kizen/Switch';
import styled from '@emotion/styled';
import Icon from '__components/Kizen/Icon';
import { useTooltip } from '__components/Kizen/Tooltip';
import { Button } from '@kizen/kds/Button';
import { Spacer } from '@kizen/kds/Spacer';
import { allowExternalCalendarsDefaultValue } from '__pages/Dashboard/util';

const StyledToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${gutters.spacing(2)}px;
`;

const StyledHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${gutters.spacing(1)}px;
`;

const StyledToggleSection = styled.div`
  display: flex;
  align-items: start;
  gap: 20px;
  width: 100%;
`;

const InfoIconStyles = styled.div`
  display: flex;
  align-items: start;
  justify-content: center;
  height: 100%;
  cursor: pointer;
  margin-bottom: 10px;
  margin-left: 5px;
`;

export const CALENDAR_TIME_PERIOD = {
  MONTH: 'month',
  WEEK: 'week',
  DAY: 'day',
};

const ScheduledActivitiesCalendarReportType = ({
  defaultName = '',
  handleSetName,
  mobile = false,
  active = false,
}) => {
  const { state, setState, entityName } = useContext(
    WizardStateContext.Context
  );
  const {
    name,
    reportType,
    calendarTimePeriod,
    showOnlyWorkingDays,
    allowExternalCalendars = allowExternalCalendarsDefaultValue,
  } = state;

  const { t } = useTranslation();

  const timePeriodMonthOption = useMemo(() => {
    return {
      label: t('Month'),
      value: CALENDAR_TIME_PERIOD.MONTH,
    };
  }, [t]);

  const timePeriodWeekOption = useMemo(() => {
    return {
      label: t('Week'),
      value: CALENDAR_TIME_PERIOD.WEEK,
    };
  }, [t]);

  const timePeriodDayOption = useMemo(() => {
    return {
      label: t('Day'),
      value: CALENDAR_TIME_PERIOD.DAY,
    };
  }, [t]);

  const timePeriodOptions = [
    timePeriodDayOption,
    timePeriodWeekOption,
    timePeriodMonthOption,
  ];

  useDefaultValues(
    {
      calendarTimePeriod: timePeriodMonthOption,
      showOnlyWorkingDays: false,
      display: 'calendar',
    },
    active && reportType?.value === REPORT_TYPES.SCHEDULED_ACTIVITIES_CALENDAR
  );

  const onChangeTimePeriod = useCallback(
    (value) => {
      return setState('calendarTimePeriod', value);
    },
    [setState]
  );

  const onChangeShowOnlyWorkingDays = useCallback(() => {
    return setState('showOnlyWorkingDays', !showOnlyWorkingDays);
  }, [setState, showOnlyWorkingDays]);

  const onChangeAllowExternalCalendars = useCallback(() => {
    return setState('allowExternalCalendars', !allowExternalCalendars);
  }, [setState, allowExternalCalendars]);

  const handleConfigureClick = useCallback(() => {
    window.open('/settings/business/calendar', '_blank');
  }, []);

  const [tooltipProps, tooltip] = useTooltip({
    label: t(
      'Mobile view will always display Day view. Users can customize this setting for their personal view.'
    ),
  });

  // Hide self if not scheduled activities calendar
  if (reportType?.value !== REPORT_TYPES.SCHEDULED_ACTIVITIES_CALENDAR) {
    return null;
  }

  return (
    <>
      <StyledWizardField top={0}>
        <StyledHeaderWrapper>
          <KizenTypography type="subheader">
            {t('Time Period View')}
          </KizenTypography>
          <InfoIconStyles>
            <Icon icon="info-circle" size="lg" {...tooltipProps} />
          </InfoIconStyles>
          {tooltip}
        </StyledHeaderWrapper>
        <StyledWizardField top={gutters.spacing(1, 1)}>
          <ButtonGroup
            value={calendarTimePeriod}
            onChange={onChangeTimePeriod}
            sizing={!mobile ? 'auto' : ''}
            options={timePeriodOptions}
            columns={mobile ? 2 : 3}
            className="wizard-buttons time-period-view"
            mode="wizard"
            gutter={`${gutters.spacing(mobile ? 2 : 3)}px`}
            data-qa-id="calendar-time-period-selector"
          />
        </StyledWizardField>
      </StyledWizardField>
      <StyledWizardField top={gutters.spacing(5)}>
        <StyledToggleSection>
          <StyledToggleWrapper>
            <Switch
              checked={showOnlyWorkingDays}
              onChange={onChangeShowOnlyWorkingDays}
              textPlacement="regular-top"
              label={t('Show Only Working Days')}
              data-qa-id="show-only-working-days-toggle"
            />
          </StyledToggleWrapper>
          <Button
            variant="text"
            size="small"
            color="secondary"
            onClick={handleConfigureClick}
            qa={{ action: 'configure-business-calendar' }}
          >
            {t('CONFIGURE')}
          </Button>
          <Spacer size={20} />
          <StyledToggleWrapper>
            <Switch
              checked={allowExternalCalendars}
              onChange={onChangeAllowExternalCalendars}
              textPlacement="regular-top"
              label={t('Allow External Calendars')}
              data-qa-id="allow-external-calendars-toggle"
              tooltip={t(
                'Shows external events for users who have integrated their calendar when enabled.'
              )}
            />
          </StyledToggleWrapper>
        </StyledToggleSection>
      </StyledWizardField>
      <TitleField
        name={name}
        defaultName={defaultName}
        onChangeName={handleSetName}
        top={gutters.spacing(3, 2)}
        entityName={entityName}
      />
    </>
  );
};

export default ScheduledActivitiesCalendarReportType;
